import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { MessageSquare, Loader2 } from "lucide-react";

interface SmsInviteDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onInviteSent?: () => void;
}

export const SmsInviteDialog = ({ isOpen, setIsOpen, onInviteSent }: SmsInviteDialogProps) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState("");
  const { toast } = useToast();

  const defaultMessage = "You're invited to join as a vendor! Check your SMS for a 6-digit verification code and visit: ";

  const handleSendInvite = async () => {
    if (!phoneNumber.trim() || !vendorName.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter both phone number and vendor name.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Format phone number (ensure it starts with country code)
      let formattedPhone = phoneNumber.trim();
      if (!formattedPhone.startsWith('+')) {
        // Assume Kenya if no country code provided
        formattedPhone = formattedPhone.startsWith('0') 
          ? `+254${formattedPhone.slice(1)}`
          : `+254${formattedPhone}`;
      }

      // Use our hybrid edge function that handles both OTP generation and SMS sending
      const { data, error } = await supabase.functions.invoke('send-sms-invitation', {
        body: {
          phoneNumber: formattedPhone,
          vendorName: vendorName,
          customMessage: customMessage.trim() || defaultMessage
        }
      });

      if (error) {
        console.error('Error sending SMS invitation:', error);
        throw new Error(error.message || "Failed to send SMS invitation");
      }

      if (!data.success) {
        throw new Error(data.error || "Failed to send SMS invitation");
      }

      // Set the generated URL for display
      if (data.testUrl) {
        setGeneratedUrl(data.testUrl);
      }

      toast({
        title: "SMS Invitation Sent!",
        description: `OTP sent to ${formattedPhone}. The vendor will receive a 6-digit code to complete registration.`,
      });
      
      // Call the callback to refresh invitations
      onInviteSent?.();
      
      // Reset form and close dialog
      setPhoneNumber("");
      setVendorName("");
      setCustomMessage("");
      setGeneratedUrl("");
      setIsOpen(false);

    } catch (error: any) {
      console.error("SMS invite error:", error);
      toast({
        title: "Failed to Send Invite",
        description: error.message || "Could not send SMS invitation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setPhoneNumber("");
      setVendorName("");
      setCustomMessage("");
      setGeneratedUrl("");
      setIsOpen(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            Invite Vendor via SMS
          </DialogTitle>
          <DialogDescription>
            Send a 6-digit verification code via SMS. The vendor will receive an OTP to complete their registration at /auth/invite.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="vendor-name">Vendor Name</Label>
            <Input
              id="vendor-name"
              placeholder="Enter vendor's name"
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone-number">Phone Number</Label>
            <Input
              id="phone-number"
              placeholder="+254700000000 or 0700000000"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Enter with country code (+254) or local format (07...)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="custom-message">Custom Message (Optional)</Label>
            <Textarea
              id="custom-message"
              placeholder="You're invited to join as a vendor! Use the 6-digit code we're sending to complete your registration."
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              disabled={isLoading}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to use default invitation message
            </p>
          </div>

          {generatedUrl && (
            <div className="space-y-2 p-3 bg-green-50 border border-green-200 rounded-md">
              <Label className="text-green-800 font-medium">Generated Invitation URL (for testing):</Label>
              <div className="p-2 bg-white border rounded text-xs font-mono break-all">
                {generatedUrl}
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigator.clipboard.writeText(generatedUrl)}
                className="w-full"
              >
                Copy URL
              </Button>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleSendInvite} disabled={isLoading}>
              {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send SMS Invite
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};